import { default as locale } from 'date-fns__legacy__/locale/pt'
import { userAvatar, fileAvatar } from 'Services/avatarGenerator'
import format from 'date-fns__legacy__/format'
import parse from 'date-fns__legacy__/parse'
import quotationDeadline from 'Enums/quotationDeadline'
import { ApiQuoteNegotiationFile, QuoteFile, ApiQuoteNegotiationDetail, QuoteNegotiationDetail } from 'Types'
import { truncateFilename, getFileExtension } from 'Services/fileService'
import first from 'lodash/first'
import { isObjectExpression } from '@babel/types'

const parseForm = (formItem) => {
  const { question, answer, inputTypeFormat, isBuildingSpecification } = formItem
  const type = inputTypeFormat.toUpperCase()

  return { question, answer, type, isBuildingSpecification }
}

const parseFile = (item: ApiQuoteNegotiationFile): QuoteFile => {
  const fileExtension = getFileExtension(item.fileName)

  return {
    isImage: item.isImage,
    title: truncateFilename(item.fileName),
    url: item.isImage ? item.imageLargeUrl : item.fileUrl,
    thumbnail: item.isImage ? item.imageSmallUrl : fileAvatar(fileExtension),
    width: item.widthImageLarge,
    height: item.heightImageLarge,
  }
}

/**
 * @param {ApiQuoteNegotiationPhone} data
 * @returns {string}
 */
const parsePhone = (data) => {
  if (Object.values(data).includes(null)) { return }

  const { ddd, number, isVerified } = data

  const firstSequence = number.slice(0, 4)
  const secondSequence = number.slice(4, 9)

  return {
    phone: `${ddd} ${firstSequence}-${secondSequence}`,
    isVerified
  }
}

/**
 * @param {ApiQuoteNegotiationPreview} data
 * @returns {QuoteNegotiationPreview}
 */
export const parseNegotiationPreview = (data) => {
  const dateCreated = parse(data.dateCreated)

  const sender = {
    id: data.senderId,
    name: data.senderName,
    occupation: data.senderOccupation,
    avatar: data.senderPhoto ? data.senderPhoto : null,
    company: data.senderCompanyName,
    phoneList: data.phones.map(parsePhone),
    rate: data.senderRate
  }

  const address = {
    name: data.address.description,
    street: data.address.street,
    number: data.address.streetNumber,
    details: data.address.complementary,
    neighborhood: data.address.neighborhood,
    state: data.address.state,
    city: data.address.city,
  }

  return {
    quotationId: data.quotationId,
    type: data.quotationType,
    negotiationId: data.companyQuotationId,
    created: format(dateCreated, 'DD/MM/YYYY [às] HH:mm', { locale }),
    hasBudget: data.hasEstimateAmount,
    newMessages: data.unreadMessagesCount,
    serviceType: data.serviceType,
    status: data.tag,
    token: data.quotationIdentifier,
    sender,
    address,
    dateCreated,
    hasCustomerEvaluation: data.hasCustomerEvaluation,
  }
}

export const parseNegotiationList = (list) => {
  const { items } = list
  const negotiationList = items.map(parseNegotiationPreview)

  return negotiationList
}

export const parseNegotiationDetails = (item: ApiQuoteNegotiationDetail): QuoteNegotiationDetail => {
  const parsedDate = parse(item.dateCreated)
  const questionList = item.answers.map(parseForm)
  return {
    negotiationId: item.companyQuotationId,
    status: item.recipientStatus,
    quotationId: item.id,
    token: item.identifier,
    category: item.category,
    serviceType: item.serviceType,
    fileList: item.files.map(parseFile),
    created: format(parsedDate, 'DD/MM/YYYY [às] HH:mm', { locale }),
    dateCreated: parsedDate,
    hasCustomerEvaluation: item.hasCustomerEvaluation,
    sender: {
      id: item.sender.id,
      name: item.sender.name,
      shortName: abbreviateSenderName(item.sender.name),
      email: item.sender.email,
      avatar: userAvatar(item.sender.photo, item.sender.name),
      phoneList: item.phones,
      rate: item.sender.rate,
      contactPreferenceDescription: item.sender.contactPreferenceDescription,
      contactPreference: item.sender.contactPreference,
      occupation: item.sender.occupation,

    },
    request: {
      questionFormList: questionList.filter(answer => answer.isBuildingSpecification === false),
      questionBuildingList: questionList.filter(answer => answer.isBuildingSpecification === true),
      details: item.description,
      deadline: quotationDeadline.getDescriptionByValue(item.periodStartOfService),
      attachments: item.files.length,
    },
    address: {
      description: item.address.description,
      street: item.address.street,
      number: item.address.streetNumber,
      details: item.address.complementary,
      neighborhood: item.address.neighborhood,
      zipcode: item.address.zipcode,
      city: item.address.city,
      state: item.address.state,
    },
  }
}

const abbreviateSenderName = (senderName) => {
  const abbreviatedLastName = first(senderName.split(' ').pop().split(''))
  const firstName = first(senderName.split(' '))
  return `${firstName} ${abbreviatedLastName}.`
}

export const parseRequestRateUser = (item) => ({
  rateId: item.id,
  negotiationId: item.idCompanyQuotation,
  rating: item.rating || 0,
  comment: item.comment || '',
  lastUpdated: '',
  reply: item.reply,
  requester: {
    id: item.customerId,
    picture: item.customerPhotoUrl || '',
    name: item.customerName,
  }
})

export const parseRequestRateList = (item) => ({
  rateId: item.id,
  negotiationId: item.idCompanyQuotation,
  score: item.rating,
  comment: item.comment || '',
  lastUpdated: '',
  reply: {
    comment: item.reply || '',
  },
  author: {
    id: item.companyName,
    avatar: item.companyPhotoUrl,
    name: item.companyName,
  }
})

export const parseRequestSenderInfo = (item) =>{
  const parsedDate = parse(item.startedDate)

  return{
    startedDate: format(parsedDate, 'DD/MM/YYYY', { locale }),
    totalEvaluations: item.totalEvaluations,
    totalQuotations: item.totalQuotations
  }
}


export default {
  parseNegotiationList,
  parseNegotiationDetails,
}